import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import ContentLayout, { H2, Prose } from '../components/ContentLayout';
import SmartImage from '../components/SmartImage';
import IMG1 from '../assets/ashram/ashram-meditation.jpg';
import IMG2 from '../assets/ashram/ashram-celebration.jpg';
import IMG_GOATS from '../assets/ashram/ashram-goats.jpg';
import IMG_MOON from '../assets/ashram/ashram-moon.jpg';
import IMG_NIGHT from '../assets/ashram/ashram-night.jpg';
import IMG_DUSK from '../assets/ashram/ashram-dusk.jpg';
import IMG_SHRINE from '../assets/ashram/ashram-shrine.jpg';
import IMG_HANUMAN from '../assets/ashram/ashram-hanuman.jpg';
import VIDEO_POSTER from '../assets/ashram/ashram-video-poster.jpg';

const IMPORTANT_DAYS = [
  ['19th January', 'Osho Mahaparinirvana Day Celebration'],
  ['21st March', 'Osho Enlightenment Day'],
  ['Full Moon of July', 'Guru Purnima (Master’s Day) Celebration'],
  ['26th September', 'Osho Neo Sannyas Day'],
  ['11th December', 'Osho Birthday Celebration'],
  ['Full Moon of May', 'Buddha Purnima & International Meditation Day'],
];

const AboutUs = () => (
  <ContentLayout
    testid="page-about-us"
    eyebrow="About Us"
    titleTop="Osho Kaivalya Dham —"
    titleItalic="A Place to Be With Yourself"
    intro="Whether you’re seeking relaxation, personal growth, or a deeper connection within, the ashram is your home — a supportive space to recharge and transform."
  >
    <div className="grid md:grid-cols-2 gap-10 items-start">
      <div>
        <H2 top="About the" italic="Ashram" />
        <Prose>
          <p>Osho Kaivalya Dham is a living, breathing Buddha Field — a spiritual energy field established in the midst of nature. The seed for this was planted in the heart of our founder Sw. Prem Maitreya many years ago, with the desire to create a space dedicated to his master Osho so that he could share.</p>
          <p>After trying for over 20 years to create a place dedicated to his master, it came to fruition with the blessing of Bodhisattva Sw. Anand Arun, a loving disciple of Osho. Kaivalya Dham is a serene and inclusive space created for anyone wishing to explore the profound world of meditation. Rooted in the timeless teachings of Osho, the center is dedicated to guiding individuals on a journey of inner discovery, emotional clarity, and spiritual growth.</p>
          <p>We offer a warm and welcoming environment where people from all walks of life can come together to experience Osho’s transformative meditation techniques — cultivating inner peace, mindfulness, and a sense of togetherness through shared meditative practices.</p>
        </Prose>
        <div className="mt-6 p-5 rounded-2xl bg-rose-soft/40 border border-rose/40">
          <div className="flex items-center gap-2 text-mahogany font-medium"><Clock className="w-4 h-4" /> Open: Monday – Sunday, 9am – 7pm EST</div>
          <p className="mt-2 text-sm text-mahogany/70">*Hours of operation are subject to change without notice — please call and check before you plan your visit. During celebrations, retreats and festivals, timings may differ.</p>
        </div>
      </div>
      <div className="grid gap-4">
        <SmartImage src={IMG1} alt="Meditation at Osho Kaivalya Dham" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG2} alt="Celebration at the ashram" className="rounded-3xl aspect-[4/3]" />
      </div>
    </div>

    <div className="mt-16">
      <H2 top="Life at the" italic="Ashram" />
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <SmartImage src={IMG_GOATS} alt="Caring for the ashram animals" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG_DUSK} alt="The meditation hut at dusk" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG_HANUMAN} alt="Hanuman statue at sunset" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG_SHRINE} alt="The Osho shrine at night" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG_MOON} alt="Moonrise over the ashram" className="rounded-3xl aspect-[4/3]" />
        <SmartImage src={IMG_NIGHT} alt="The meditation hut at night" className="rounded-3xl aspect-[4/3]" />
      </div>
    </div>

    <div className="mt-16">
      <H2 top="A Glimpse of the" italic="Ashram" />
      <div className="mt-4 max-w-3xl mx-auto overflow-hidden rounded-3xl bg-rose-soft border border-rose/40">
        <video
          data-testid="ashram-tour-video"
          controls
          playsInline
          preload="metadata"
          poster={VIDEO_POSTER}
          className="w-full h-auto max-h-[70vh] bg-black"
        >
          <source src={`${process.env.PUBLIC_URL || ''}/media/ashram-tour.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    </div>

    <div className="mt-16">
      <H2 top="Vision &" italic="Mission" />
      <Prose>
        <h3 className="font-serif-display text-2xl text-burgundy" data-testid="about-vision-heading">Our Vision</h3>
        <p data-testid="about-vision-text">To bring love (Prem) and awareness (Dhyan) into every home and every heart, inspiring the awakening of every being on Earth.</p>
        <h3 className="font-serif-display text-2xl text-burgundy" data-testid="about-mission-heading">Our Mission</h3>
        <p data-testid="about-mission-text">Inspired by Osho’s teachings, we create welcoming spaces for friendship, love, and meditation. Guided by Sw. Prem Maitreya and visiting Osho disciples, our sessions and retreats help people deepen awareness, discover inner peace, and carry the joy of meditation into their homes, relationships, and everyday lives.</p>
      </Prose>
    </div>

    <div className="mt-16">
      <H2 top="Important Days of the" italic="Ashram" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {IMPORTANT_DAYS.map(([date, label]) => (
          <div key={date} className="p-5 rounded-2xl bg-white/70 border border-rose/40">
            <div className="flex items-center gap-2 text-rose-italic"><Calendar className="w-4 h-4" /> <span className="font-serif-display text-xl text-burgundy">{date}</span></div>
            <p className="mt-2 text-sm text-burgundy/80">{label}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-16" data-testid="about-organization">
      <H2 top="Our" italic="Organization" />
      <Prose>
        <p>Osho Kaivalya Dham and Osho Universal operate under <strong>Meditative Mindfulness Inc</strong>, a registered 501(c)(3) nonprofit organization. Your contributions help keep meditation and community accessible to all and may be tax-deductible to the extent allowed by law.</p>
      </Prose>
    </div>
  </ContentLayout>
);

export default AboutUs;
